import { useEffect, useRef } from 'react';

// On iOS Safari (and to a lesser extent Android Chrome) the on-screen
// keyboard can leave layout stuck against the keyboard-open viewport
// when dismissed mid-modal — visible as a "ghost" gray band where the
// keyboard used to be. The reporter's hint that "scrolling around fixes
// it" pins the cause: cached body/document heights from the
// keyboard-open viewport that only invalidate on scroll.
//
// Two triggers, because each catches a case the other misses:
//   - VisualViewport.resize on a meaningful height increase — fires
//     when the keyboard collapses via close button, scroll, native
//     back gesture. iOS sometimes batches this into multiple small
//     deltas that never cross the threshold, hence the second trigger.
//   - document `focusout` on inputs/textareas — fires reliably when
//     the user taps the background to dismiss the keyboard. Skipped
//     when focus moves to another form control (typing flow) to avoid
//     thrash on tab-between-inputs.
//
// Nudge work (idempotent, cheap):
//   1. Blur the focused element so iOS commits the keyboard close.
//   2. Toggle min-height on both <html> and <body>, force a sync
//      reflow — invalidates every cached layout height that iOS held
//      onto from the keyboard-open viewport. The original hook only
//      touched <html>, which wasn't enough in practice.
//   3. One-pixel scroll bounce — the trick the user discovered by
//      accident. Forces iOS to re-render against the new viewport.
//   4. Synthetic resize event so listeners that key off viewport size
//      also recompute (Google Maps, ResizeObserver-backed components).
const KEYBOARD_COLLAPSE_DELTA_PX = 100;

function nudgeLayout(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  (document.activeElement as HTMLElement | null)?.blur?.();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const html = document.documentElement;
      const body = document.body;
      const htmlPrev = html.style.minHeight;
      const bodyPrev = body.style.minHeight;
      html.style.minHeight = '100.001%';
      body.style.minHeight = '100.001%';
      void html.offsetHeight;
      html.style.minHeight = htmlPrev;
      body.style.minHeight = bodyPrev;
      void body.offsetHeight;
      // One-pixel scroll bounce matches the workaround the reporter
      // observed accidentally fixing the issue. scrollBy clamps to 0
      // when at the top, so on short pages this is a no-op.
      const startY = window.scrollY;
      window.scrollBy(0, 1);
      window.scrollTo({ top: startY, behavior: 'instant' as ScrollBehavior });
      window.dispatchEvent(new Event('resize'));
    });
  });
}

function isFormControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useKeyboardDismissNudge(): void {
  const lastHeight = useRef<number | null>(null);

  useEffect(() => {
    const vv = window.visualViewport;

    const onVvResize = () => {
      if (!vv) return;
      const prev = lastHeight.current ?? vv.height;
      const delta = vv.height - prev;
      lastHeight.current = vv.height;
      if (delta < KEYBOARD_COLLAPSE_DELTA_PX) return;
      nudgeLayout();
    };

    const onFocusOut = (e: FocusEvent) => {
      // Fire only when the keyboard is actually going away. Skip when
      // focus jumps between inputs (the user is still typing).
      if (!isFormControl(e.target)) return;
      if (isFormControl(e.relatedTarget)) return;
      nudgeLayout();
    };

    if (vv) {
      lastHeight.current = vv.height;
      vv.addEventListener('resize', onVvResize);
    }
    document.addEventListener('focusout', onFocusOut, true);

    return () => {
      if (vv) vv.removeEventListener('resize', onVvResize);
      document.removeEventListener('focusout', onFocusOut, true);
    };
  }, []);
}
